import React, { useState, Component } from 'react';
import { SettingsIcon, ShieldCheckIcon, UsersIcon, KeyRoundIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, LockIcon, FingerprintIcon, ScrollTextIcon, UserCogIcon } from 'lucide-react';
type SubTab = 'gerer' | 'analytiques' | 'configurer';
type GererSection = 'roles' | 'autorisations' | 'utilisateurs';
type ConfigSection = 'securite' | 'authentification' | 'journalisation';
interface Role {
  id: string;
  nom: string;
  description: string;
  statut: 'actif' | 'inactif';
}
interface Utilisateur {
  id: string;
  nom: string;
  email: string;
  roleId: string;
}
const ROLES: Role[] = [{
  id: 'admin',
  nom: 'Administrateur',
  description: 'Accès complet à tous les modules et paramètres',
  statut: 'actif'
}, {
  id: 'comm',
  nom: 'Gestionnaire Communications',
  description: 'Création et diffusion des avis municipaux',
  statut: 'actif'
}, {
  id: 'agent',
  nom: 'Agent Requêtes',
  description: 'Traitement des requêtes citoyennes',
  statut: 'actif'
}, {
  id: 'lecture',
  nom: 'Lecture seule',
  description: 'Consultation des données sans modification',
  statut: 'inactif'
}];
const MODULES: {
  id: string;
  label: string;
}[] = [{
  id: 'communications',
  label: 'Communications'
}, {
  id: 'requetes',
  label: 'Requêtes'
}, {
  id: 'id',
  label: 'ID'
}, {
  id: 'collectes',
  label: 'Collectes'
}, {
  id: 'compteurs',
  label: 'Compteurs'
}, {
  id: 'atna',
  label: 'ATNA AI'
}, {
  id: 'parametres',
  label: 'Paramètres'
}];
const ACTIONS = ['Voir', 'Créer', 'Modifier', 'Supprimer'] as const;
const INITIAL_USERS: Utilisateur[] = [{
  id: '1',
  nom: 'Hugo Langlais',
  email: 'hugo.langlais@brossard.ca',
  roleId: 'admin'
}, {
  id: '2',
  nom: 'Marie Tremblay',
  email: 'marie.tremblay@brossard.ca',
  roleId: 'comm'
}, {
  id: '3',
  nom: 'Jean Dupont',
  email: 'jean.dupont@brossard.ca',
  roleId: 'agent'
}, {
  id: '4',
  nom: 'Sarah Connor',
  email: 'sarah.connor@brossard.ca',
  roleId: 'agent'
}, {
  id: '5',
  nom: 'Belanor Waesceran',
  email: 'belanor.w@brossard.ca',
  roleId: 'lecture'
}];
// Default permissions per role
function buildInitialPermissions(): Record<string, boolean> {
  const perms: Record<string, boolean> = {};
  MODULES.forEach(m => {
    ACTIONS.forEach(a => {
      // admin: everything; lecture: only Voir; others: Voir+Créer+Modifier on a couple modules
      perms[`admin:${m.id}:${a}`] = true;
      perms[`lecture:${m.id}:${a}`] = a === 'Voir';
      perms[`comm:${m.id}:${a}`] = m.id === 'communications' ? a !== 'Supprimer' : a === 'Voir';
      perms[`agent:${m.id}:${a}`] = m.id === 'requetes' ? a !== 'Supprimer' : a === 'Voir';
    });
  });
  return perms;
}
interface SwitchProps {
  checked: boolean;
  onChange: () => void;
}
function Switch({
  checked,
  onChange
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} onClick={onChange} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-5538" data-mp="31wrfjd02t88i">
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-5539" data-mp="3ku2booqevljq" />
    </button>;
}
export function SettingsModule() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('gerer');
  const [gererSection, setGererSection] = useState<GererSection>('roles');
  const [configSection, setConfigSection] = useState<ConfigSection>('securite');
  const [roles] = useState<Role[]>(ROLES);
  const [users, setUsers] = useState<Utilisateur[]>(INITIAL_USERS);
  const [permissions, setPermissions] = useState<Record<string, boolean>>(buildInitialPermissions);
  const [selectedRole, setSelectedRole] = useState<string>('admin');
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: 'Gérer'
  }, {
    id: 'analytiques',
    label: 'Analytiques'
  }, {
    id: 'configurer',
    label: 'Configurer'
  }];
  const gererSections: {
    id: GererSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
  }[] = [{
    id: 'roles',
    label: 'Rôles',
    icon: ShieldCheckIcon
  }, {
    id: 'autorisations',
    label: 'Autorisations',
    icon: KeyRoundIcon
  }, {
    id: 'utilisateurs',
    label: 'Utilisateurs',
    icon: UsersIcon
  }];
  const roleName = (id: string) => roles.find(r => r.id === id)?.nom ?? '—';
  const countPerms = (roleId: string) => Object.keys(permissions).filter(k => k.startsWith(`${roleId}:`) && permissions[k]).length;
  const countUsers = (roleId: string) => users.filter(u => u.roleId === roleId).length;
  const togglePerm = (key: string) => setPermissions(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  // ---------- GÉRER ----------
  const renderRoles = () => <div className="space-y-4" data-id="element-5540" data-mp="2yspg543jqngr">
      <div className="flex items-center justify-between" data-id="element-5541" data-mp="2yreb70yd79o2">
        <div data-id="element-5542" data-mp="2uqvmhfrrqjq3">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5543" data-mp="2urdn0olgrk3t">Rôles</h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-5544" data-mp="2priw4l1ouuir">
            Définissez des rôles puis attribuez-leur des autorisations.
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-5545" data-mp="2ksoe3lbjp9zp">
          <PlusIcon className="w-4 h-4" data-id="element-5546" data-mp="j1bbx40ypg7c" /> Nouveau rôle
        </button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5547" data-mp="e1oxir9edtwy">
        <table className="w-full text-left" data-id="element-5548" data-mp="dzvtljq65nsn">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5549" data-mp="28gvun53k8yal">
            <tr data-id="element-5550" data-mp="2d8djiusb78f7">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5551" data-mp="2d8x28dpsa0ua">
                Rôle
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-5552" data-mp="1ylkecici7j0q">
                Autorisations
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-5553" data-mp="2r854xgl5nr38">
                Usagers
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5554" data-mp="2w559to69qnsr">
                Statut
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-5555" data-mp="2m8adnbxgwzhn">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5556" data-mp="1aueyw4ah6sqy">
            {roles.map(r => <tr key={r.id} className="hover:bg-slate-50" data-id="element-5557" data-mp="13a4ci11e7xhj">
                <td className="px-4 py-3" data-id="element-5558" data-mp="13cqihw8my4xn">
                  <div className="font-semibold text-slate-900 text-sm" data-id="element-5559" data-mp="yc2m0v1n0vw1">
                    {r.nom}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5560" data-mp="1d7ij78xil7wn">
                    {r.description}
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-5561" data-mp="kk5uot7smq0p">
                  {countPerms(r.id)}
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-5562" data-mp="fme2rz75f3wi">
                  {countUsers(r.id)}
                </td>
                <td className="px-4 py-3" data-id="element-5563" data-mp="pj90ifvll09q">
                  <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${r.statut === 'actif' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-5564" data-mp="pi7ni5v0u3pd">
                    {r.statut}
                  </span>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-5565" data-mp="3cfhedkdfo500">
                  <div className="flex items-center justify-end gap-2" data-id="element-5566" data-mp="3cfzmpfdde9oa">
                    <button onClick={() => {
                  setSelectedRole(r.id);
                  setGererSection('autorisations');
                }} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Configurer les autorisations" data-id="element-5567" data-mp="3hdrabx6zf9mo">
                      <KeyRoundIcon className="w-4 h-4" data-id="element-5568" data-mp="40z55fbrajet" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Modifier" data-id="element-5569" data-mp="8zac9iao2ftc">
                      <PencilIcon className="w-4 h-4" data-id="element-5570" data-mp="i0qn1r5e8jy3" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Supprimer" data-id="element-5571" data-mp="22i9cyfkrkoco">
                      <TrashIcon className="w-4 h-4" data-id="element-5572" data-mp="1tg9urjy0je85" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderAutorisations = () => <div className="space-y-5" data-id="element-5573" data-mp="2la23m3cd1xh8">
      <div data-id="element-5574" data-mp="2h6459uuv0qp8">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5575" data-mp="2h6dydrb7bdz5">Autorisations</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-5576" data-mp="2cfns8buyb73e">
          Choisissez un rôle et accordez-lui des autorisations par module.
        </p>
      </div>

      {/* Role selector */}
      <div className="flex flex-wrap gap-2" data-id="element-5577" data-mp="27f001muzkk7l">
        {roles.map(r => <button key={r.id} onClick={() => setSelectedRole(r.id)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${selectedRole === r.id ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-5578" data-mp="34xjapwhxno4p">
            {r.nom}
          </button>)}
      </div>

      {/* Permission matrix */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5579" data-mp="w0u2h7y9x717">
        <table className="w-full text-left" data-id="element-5580" data-mp="s3o83dqa0en1">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5581" data-mp="160kvf576rdz6">
            <tr data-id="element-5582" data-mp="1129lkugw53gy">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5583" data-mp="112rqefhmcq0h">
                Module
              </th>
              {ACTIONS.map(a => <th key={a} className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center w-28" data-id="element-5584" data-mp="1as2ufnpnf4hi">
                  {a}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5585" data-mp="1jukfvzxhbt3g">
            {MODULES.map(m => <tr key={m.id} className="hover:bg-slate-50" data-id="element-5586" data-mp="23fcbyjhzr9gc">
                <td className="px-4 py-3 text-sm font-medium text-slate-800" data-id="element-5587" data-mp="28dw2kx7j59f1">
                  {m.label}
                </td>
                {ACTIONS.map(a => {
              const key = `${selectedRole}:${m.id}:${a}`;
              return <td key={a} className="px-4 py-3 text-center" data-id="element-5588" data-mp="1yhaug307ri6z">
                      <div className="flex justify-center" data-id="element-5589" data-mp="1kmgusowzhzwe">
                        <Switch checked={!!permissions[key]} onChange={() => togglePerm(key)} data-id="element-5590" data-mp="1pfa0bf0a1c9j" />
                      </div>
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end" data-id="element-5591" data-mp="11qifj8p0nw6s">
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-5592" data-mp="16oaf8ovwkmlj">
          Enregistrer les autorisations
        </button>
      </div>
    </div>;
  const renderUtilisateurs = () => <div className="space-y-4" data-id="element-5593" data-mp="33lxyjwarq3m1">
      <div data-id="element-5594" data-mp="33kmv5xl8ixvg">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5595" data-mp="2ynmnxjcnfp2r">Utilisateurs</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-5596" data-mp="2ynweb81lvy8z">
          Assignez un rôle à chaque utilisateur.
        </p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5597" data-mp="3rcbsjscwycj9">
        <table className="w-full text-left" data-id="element-5598" data-mp="3ra7onaf0ldiz">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5599" data-mp="3mcz5p7m1l5go">
            <tr data-id="element-5600" data-mp="3ibm08ymv34aa">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5601" data-mp="3ib46c8no925j">
                Utilisateur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5602" data-mp="3dc9eyibn259t">
                Courriel
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-64" data-id="element-5603" data-mp="21xnj9zzmdapg">
                Rôle assigné
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5604" data-mp="mefvf04aycij">
            {users.map(u => <tr key={u.id} className="hover:bg-slate-50" data-id="element-5605" data-mp="qg1cyyrs7ez2">
                <td className="px-4 py-3" data-id="element-5606" data-mp="qegita9dpy1q">
                  <div className="flex items-center gap-3" data-id="element-5607" data-mp="v6q42czju9xo">
                    <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold" data-id="element-5608" data-mp="v8b1cad906z8">
                      {u.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-sm font-medium text-slate-800" data-id="element-5609" data-mp="1e5lztsr22wdm">
                      {u.nom}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-5610" data-mp="3bxghhfdqch3o">{u.email}</td>
                <td className="px-4 py-3" data-id="element-5611" data-mp="3fxh43nazfylt">
                  <select value={u.roleId} onChange={e => setUsers(prev => prev.map(x => x.id === u.id ? {
                ...x,
                roleId: e.target.value
              } : x))} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm cursor-pointer focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-5612" data-mp="2kmsic0k9um1g">
                    {roles.map(r => <option key={r.id} value={r.id} data-id="element-5613" data-mp="26ulzg6pqi0yb">
                        {r.nom}
                      </option>)}
                  </select>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  // ---------- ANALYTIQUES ----------
  const renderAnalytiques = () => {
    const totalPerms = roles.reduce((acc, r) => acc + countPerms(r.id), 0);
    const kpis = [{
      label: 'Rôles actifs',
      value: String(roles.filter(r => r.statut === 'actif').length),
      icon: ShieldCheckIcon
    }, {
      label: 'Utilisateurs',
      value: String(users.length),
      icon: UsersIcon
    }, {
      label: 'Autorisations accordées',
      value: String(totalPerms),
      icon: KeyRoundIcon
    }, {
      label: 'Rôles configurés',
      value: String(roles.length),
      icon: UserCogIcon
    }];
    const maxUsers = Math.max(...roles.map(r => countUsers(r.id)), 1);
    return <div className="space-y-8" data-id="element-5614" data-mp="8foiya8djiq6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5615" data-mp="2o46qsqere9d9">
          {kpis.map(k => {
          const Icon = k.icon;
          return <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5616" data-mp="2t54870ry2zdp">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center mb-3" data-id="element-5617" data-mp="torf8g8trrmb">
                  <Icon className="w-5 h-5" data-id="element-5618" data-mp="owzsyhwpc81z" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A]" data-id="element-5619" data-mp="kw615dg5293t">
                  {k.value}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-5620" data-mp="fxmgdgda73d1">{k.label}</div>
              </div>;
        })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-5621" data-mp="awf5v9jr7q2o">
          <h3 className="text-base font-bold text-[#0F172A] mb-4" data-id="element-5622" data-mp="5y5c93dogxj6">
            Répartition des utilisateurs par rôle
          </h3>
          <div className="space-y-3" data-id="element-5623" data-mp="1ywkg42779q9">
            {roles.map(r => {
            const c = countUsers(r.id);
            return <div key={r.id} className="flex items-center gap-3" data-id="element-5624" data-mp="1wbctfcmdp1yt">
                  <span className="text-sm text-slate-600 w-56 truncate" data-id="element-5625" data-mp="1w98i49m5h0oi">
                    {r.nom}
                  </span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5626" data-mp="3j82jqfmn4rv3">
                    <div className="h-full bg-[#4F46E5] rounded-full" style={{
                  width: `${c / maxUsers * 100}%`
                }} data-id="element-5627" data-mp="3nyrdy2ppg2xx"></div>
                  </div>
                  <span className="text-sm font-semibold text-slate-700 w-6 text-right" data-id="element-5628" data-mp="2vbez6epsczf6">
                    {c}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  // ---------- CONFIGURER ----------
  const configSections: {
    id: ConfigSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
    desc: string;
  }[] = [{
    id: 'securite',
    label: 'Sécurité',
    icon: LockIcon,
    desc: 'Politique de mot de passe et de session'
  }, {
    id: 'authentification',
    label: 'Authentification',
    icon: FingerprintIcon,
    desc: 'Méthodes de connexion et MFA'
  }, {
    id: 'journalisation',
    label: 'Journalisation',
    icon: ScrollTextIcon,
    desc: 'Audit et conservation des journaux'
  }];
  const [mfa, setMfa] = useState(true);
  const [sso, setSso] = useState(false);
  const [auditLog, setAuditLog] = useState(true);
  const renderConfigContent = () => {
    if (configSection === 'securite') {
      return <div className="space-y-5 max-w-lg" data-id="element-5629" data-mp="dxfhtw0utvfc">
          <div data-id="element-5630" data-mp="e0uu6r358qd2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5631" data-mp="rxzzgov19l79">
              Longueur minimale du mot de passe
            </label>
            <input type="number" defaultValue={12} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5632" data-mp="nx6b5udoi4du" />
          </div>
          <div data-id="element-5633" data-mp="2pqdwpljvxi86">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5634" data-mp="2fkcsjkaopeey">
              Expiration de la session (minutes)
            </label>
            <input type="number" defaultValue={30} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5635" data-mp="2jl6ggdo4mt7u" />
          </div>
        </div>;
    }
    if (configSection === 'authentification') {
      return <div className="space-y-4 max-w-lg" data-id="element-5636" data-mp="1vygd9901ksln">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5637" data-mp="20w9j20xfnkle">
            <div data-id="element-5638" data-mp="1mwgv2ddajih4">
              <p className="text-sm font-semibold text-slate-800" data-id="element-5639" data-mp="1mx04t6ssf0k2">
                Authentification à deux facteurs (MFA)
              </p>
              <p className="text-xs text-slate-500" data-id="element-5640" data-mp="3h9ghapc6faxd">
                Exiger un second facteur à la connexion.
              </p>
            </div>
            <Switch checked={mfa} onChange={() => setMfa(!mfa)} data-id="element-5641" data-mp="3m789lkgqgz23" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5642" data-mp="tn1rb3xiketo">
            <div data-id="element-5643" data-mp="yebdtvnrn21c">
              <p className="text-sm font-semibold text-slate-800" data-id="element-5644" data-mp="yetklm820yni">
                Authentification unique (SSO)
              </p>
              <p className="text-xs text-slate-500" data-id="element-5645" data-mp="jn2m9h0az6lk">
                Connexion via le fournisseur d'identité de la ville.
              </p>
            </div>
            <Switch checked={sso} onChange={() => setSso(!sso)} data-id="element-5646" data-mp="1ceumn5022c4k" />
          </div>
        </div>;
    }
    return <div className="space-y-4 max-w-lg" data-id="element-5647" data-mp="1hce1a60o0qx1">
        <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5648" data-mp="13demdq2yfqe2">
          <div data-id="element-5649" data-mp="17egb3cz8wk1s">
            <p className="text-sm font-semibold text-slate-800" data-id="element-5650" data-mp="17dyj4sjm8rzo">
              Journal d'audit
            </p>
            <p className="text-xs text-slate-500" data-id="element-5651" data-mp="3o2ap0mlxjuh8">
              Enregistrer toutes les actions des administrateurs.
            </p>
          </div>
          <Switch checked={auditLog} onChange={() => setAuditLog(!auditLog)} data-id="element-5652" data-mp="3s2m8vx6gxqrn" />
        </div>
        <div data-id="element-5653" data-mp="3knixgn083z0b">
          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5654" data-mp="3fmd4qp6kcbqs">
            Durée de conservation des journaux (jours)
          </label>
          <input type="number" defaultValue={365} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5655" data-mp="3uiawzwdd8vxq" />
        </div>
      </div>;
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5656" data-mp="2wvvbmjhtje4w">
      {/* Header */}
      <div className="mb-8" data-id="element-5657" data-mp="2wu0snaq3xw3g">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-id="element-5658" data-mp="3bl9m5ux4s1lv">
          <div className="flex items-center gap-3" data-id="element-5659" data-mp="36thx5otmi3xu">
            <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center" data-id="element-5660" data-mp="36sqcva5h7smv">
              <SettingsIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-5661" data-mp="2j2mat77xs0xz" />
            </div>
            <div data-id="element-5662" data-mp="2e4knga3h9fgp">
              <h1 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-5663" data-mp="2e540bce9tdnv">
                Paramètres
              </h1>
              <p className="text-sm text-slate-500" data-id="element-5664" data-mp="37v3bg3rqz7uy">
                Système d'autorisations par rôles
              </p>
            </div>
          </div>
          <nav aria-label="Sections" className="flex flex-wrap items-center gap-2" data-id="element-5665" data-mp="3cn4hv928i4d2">
            {tabs.map(t => <button key={t.id} onClick={() => setActiveSubTab(t.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === t.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5666" data-mp="2kux20aujyvzq">
                {t.label}
              </button>)}
          </nav>
        </div>
      </div>

      {activeSubTab === 'analytiques' ? renderAnalytiques() : activeSubTab === 'configurer' ? <div className="flex flex-col lg:flex-row gap-6" data-id="element-5667" data-mp="1ylynkbau2954">
          <div className="lg:w-60 flex-shrink-0" data-id="element-5668" data-mp="1ylgicp670kl2">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-5669" data-mp="1tmltb5hmtzsi">
              {configSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setConfigSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${configSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5670" data-mp="1jwao7fm7pkk1">
                    <Icon className={`w-4 h-4 ${configSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-5671" data-mp="10yzs23vvn78t" />
                    {s.label}
                  </button>;
          })}
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-5672" data-mp="33d6xftt4ojwk">
            <div className="mb-6" data-id="element-5673" data-mp="33b3w4dmiz4cj">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5674" data-mp="38ct9w114tw4b">
                {configSections.find(s => s.id === configSection)?.label}
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-5675" data-mp="2thd9zfdvf600">
                {configSections.find(s => s.id === configSection)?.desc}
              </p>
            </div>
            {renderConfigContent()}
            <div className="mt-8" data-id="element-5676" data-mp="2pdfc180a818j">
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-5677" data-mp="2pdntzeb9hwql">
                Enregistrer
              </button>
            </div>
          </div>
        </div> :
    // GÉRER
    <div className="flex flex-col lg:flex-row gap-6" data-id="element-5678" data-mp="21nte0nvnbs24">
          <div className="lg:w-60 flex-shrink-0" data-id="element-5679" data-mp="21ocyoccokuju">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-5680" data-mp="25p6k8z2nhx9k">
              {gererSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setGererSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${gererSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5681" data-mp="1i2t7ej2hhih7">
                    <Icon className={`w-4 h-4 ${gererSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-5682" data-mp="2115g63phgqc9" />
                    {s.label}
                  </button>;
          })}
              <div className="px-4 pt-3 pb-1" data-id="element-5683" data-mp="2as037aajztyr">
                <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-5684" data-mp="2oqhol1czkudi">
                  1. Créez un rôle · 2. Accordez des autorisations · 3.
                  Assignez-le à des usagers.
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8 min-w-0" data-id="element-5685" data-mp="1hfjznjrwelbr">
            {gererSection === 'roles' && renderRoles()}
            {gererSection === 'autorisations' && renderAutorisations()}
            {gererSection === 'utilisateurs' && renderUtilisateurs()}
          </div>
        </div>}
    </div>;
}